import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { IMaskInput } from 'react-imask';
import GA from '../../lib/ga';

import { MaskedField } from '../shared/MaskedField';
import { validationStyles } from '../../constants/SiteVariables';
import { createPrequalification } from '../../actions/prequalifications';

const ga = new GA();

const ficoRange = [{
    scale: null,
    label: '--'
  }, {
    scale: 1,
    label: 'Poor (<600)'
  }, {
    scale: 2,
    label: 'Fair (600-640)'
  }, {
    scale: 3,
    label: 'Good (640-680)'
  }, {
    scale: 4,
    label: 'Very Good (680-724)'
  }, {
    scale: 5,
    label: 'Excellent (725+)'
  }];

export default function PrequalForm({ match }) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [prequalData, setPrequalData] = useState({
    company_name: '',
    gross_annual_revenue: '0',
    owner_first_name: '',
    owner_last_name: '',
    email: '',
    mobile: '',
    fico_scale: '',
    street: '',
    city: '',
    state: '',
    zip: ''
  });

  const save = values => {
    setIsLoading(true);
    dispatch(createPrequalification(values))
      .then((res) => {
        const { status, estimated_rate, estimated_tcl } = res.payload.data.attributes;
        if (status === 'pass') {
          ga.push('pdp_prequalify_pass', {
            'estimated_rate': estimated_rate,
            'estimated_tcl': estimated_tcl,
          });
        } else if (status === 'fail') {
          ga.push('pdp_prequalify_fail');
        }
      })
      .then(() => setIsLoading(false));
  }

  return (
    <Formik
      // the values that come off the API are null
      // and need to be reset here to cooperate w/ formik
      initialValues={prequalData}
      validate={async (values) => {
        const errors = {};

        if (!values.owner_first_name) errors.owner_first_name = '* Required';
        if (!values.owner_last_name) errors.owner_last_name = '* Required';
        if (!values.fico_scale) errors.fico_scale = '* Required';

        if (!values.mobile) {
          errors.mobile = '* Required';
        } else if (values.mobile.replace(/[^\d]/g, '').length < 10) {
          errors.mobile = '* Please enter a valid phone number';
        }

        if (!values.company_name) {
          errors.company_name = '* Required';
        } else if (values.company_name.trim().length < 2) {
          errors.company_name = '* Company Name must be at least 2 characters';
        }

        if (!values.email) {
          errors.email = 'Required'
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
          errors.email = '* Invalid email address';
        }

        return errors;
      }}
      onSubmit={async (values, { setFieldError }) => {
        return save(values);
      }}
    >
      {({ isSubmitting, errors }) => (
        <Form>
          <div className="has-text-centered has-text-weight-bold is-size-4">Let's make this quick</div>
          <div className="has-text-centered has-text-grey mb-5">Remember: applying will not affect your credit</div>

          <div role="columns" className="columns mb-3 is-mobile">
            <div className="column is-half">
              <div className="field sticky-placeholder">
                <div className="control">
                  <Field
                    className={classNames('input', {
                      'is-danger': errors.owner_first_name
                    })}
                    type="text"
                    id="owner_first_name"
                    name="owner_first_name"
                    placeholder="Owner First Name"/>
                  <label className="placeholder" htmlFor="owner_first_name">Owner First Name</label>
                  <ErrorMessage name="owner_first_name" component="div" className={validationStyles} />
                </div>
              </div>
            </div>

            <div className="column">
              <div className="field sticky-placeholder">
                <div className="control">
                  <Field
                    className={classNames('input', {
                      'is-danger': errors.owner_last_name
                    })}
                    type="text"
                    id="owner_last_name"
                    name="owner_last_name"
                    placeholder="Owner Last Name" />
                  <label className="placeholder" htmlFor="owner_last_name">Owner Last Name</label>
                  <ErrorMessage name="owner_last_name" component="div" className={validationStyles} />
                </div>
              </div>
            </div>
          </div>

          <div className="columns mb-3 is-mobile">
            <div className="column is-half">
              <div className="field sticky-placeholder">
                <div className="control">
                  <Field
                    className={classNames('input', {
                      'is-danger': errors.email
                    })}
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Owner Email" />
                  <label className="placeholder" htmlFor="email">Owner Email</label>
                  <ErrorMessage name="email" component="div" className={validationStyles} />
                </div>
              </div>
            </div>

            <div className="column">
              <div className="field sticky-placeholder">
                <div className="control">
                  <MaskedField
                    className={classNames('input', {
                      'is-danger': errors.mobile
                    })}
                    type="text"
                    id="mobile"
                    name="mobile"
                    mask="(999) 999-9999"
                    placeholder="Mobile Phone Number" />
                  <label className="placeholder" htmlFor="mobile">Mobile Phone number</label>
                  <ErrorMessage name="mobile" component="div" className={validationStyles} />
                </div>
              </div>
            </div>
          </div>

          <div role="columns" className="columns mb-3 is-mobile">
            <div className="column">
              <div className="field sticky-placeholder">
                <div className="control">
                  <Field
                    className={classNames('input', {
                      'is-danger': errors.company_name
                    })}
                    type="text"
                    id="company_name"
                    name="company_name"
                    placeholder="Company Name"/>
                  <label className="placeholder" htmlFor="company_name">Company Name</label>
                  <ErrorMessage name="company_name" component="div" className={validationStyles} />
                </div>
              </div>
            </div>
          </div>

          <div className="columns is-mobile">
            <div className="column">
              <div className="field sticky-placeholder">
                <div className="control">
                  <MaskedField
                    className={classNames('input', {
                      'is-danger': errors.gross_annual_revenue
                    })}
                    type="text"
                    inputMode="numeric"
                    id="gross_annual_revenue"
                    name="gross_annual_revenue"
                    mask={Number}
                    thousandsSeparator=","
                    scale={0}
                    placeholder="$ Total Annual Revenue" />
                  <label className="placeholder" htmlFor="gross_annual_revenue">$ Total Annual Revenue</label>
                  <ErrorMessage name="gross_annual_revenue" component="div" className={validationStyles} />
                </div>
              </div>
            </div>
          </div>

          <div className="columns is-mobile">
            <div className="column">
              <div className="field sticky-placeholder select is-fullwidth">
                <div className="control">
                  <Field as="select"
                      className={classNames('input', {
                        'is-danger': errors.fico_scale
                      })}
                      children={ficoRange.map(r => <option key={r.scale} value={r.scale}>{r.label}</option>)}
                      id="fico_scale"
                      name="fico_scale"
                      placeholder="Estimated FICO"/>
                  <label className="placeholder" htmlFor="fico_scale">Estimated FICO</label>
                  <ErrorMessage name="fico_scale" component="div" className={validationStyles} />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            role="submit"
            disabled={isLoading}
            className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
              'is-loading': isLoading
            })}>
            Next
          </button>
        </Form>
      )}
    </Formik>
  );
}
